import React from 'react';
import styled, {css} from 'styled-components';
import {useIsRTL} from '@aboutyou/i18n';
import {TransitionComponentType, Slide, Fade} from '@aboutyou/transitions';
import {COLORS} from '../../../../../constants/colors';
import {AboutYouHeartIcon, AboutYouKnob} from './AboutYouIcon';

// This is defined based on a wide letter (O) and narrowest viewport available in chrome devtools (Iphone SE)
// it keeps meaningful space around the logo and prevents horizontal scroll
const DEFAULT_MAX_LENGTH = 18;

export const AboutNameTextLogo: React.FC<{
  firstName?: string;
  maxLength?: number;
  showHeartIcon?: boolean;
  FirstNameAnimationComponent?: TransitionComponentType;
}> = React.memo(function AboutNameTextLogo({
  firstName,
  maxLength = DEFAULT_MAX_LENGTH,
  showHeartIcon,
  FirstNameAnimationComponent = Slide,
}) {
  const showFirstName = Boolean(firstName);
  const isRTL = useIsRTL();

  const LogoIcon = showHeartIcon
    ? StyledAboutYouHeartWhite
    : StyledAboutYouKnob;

  return (
    <Wrapper $isRTL={isRTL}>
      <AboutText data-testid="aboutLogo" $isRTL={isRTL}>
        ABOUT
      </AboutText>
      <UserNameWrapper data-testid="userNameWrapper" data-dd-privacy="mask">
        <Fade mounted={!firstName} duration={{mount: 0, unmount: 500}}>
          <YouText data-testid="you" $isRTL={isRTL}>
            YOU
            <LogoIcon $isRTL={isRTL} />
          </YouText>
        </Fade>
        <FirstNameAnimationComponent
          mounted={showFirstName}
          duration={1000}
          direction="left"
          transformLength={25}
        >
          <StyledLogoText
            data-testid="userName"
            $isRTL={isRTL}
            data-dd-privacy="mask"
          >
            {firstName?.slice(0, maxLength)}
            <LogoIcon $isRTL={isRTL} />
          </StyledLogoText>
        </FirstNameAnimationComponent>
      </UserNameWrapper>
    </Wrapper>
  );
});

const Wrapper = styled.div<{$isRTL: boolean}>`
  display: flex;
  position: relative;
  min-width: 160px;
  max-height: inherit;
  ${({$isRTL}) => $isRTL && 'direction: initial;'}
`;

export const LogoText = styled.span`
  display: flex;
  font-family: 'Open Sans Condensed Bold', sans-serif;
  text-transform: uppercase;
  font-size: 30px;
  font-weight: 700;
  padding: 0 4px;
  color: ${COLORS.White};
  background-color: ${COLORS.Black};
  white-space: nowrap;
  direction: initial;
  display: inline;
  max-height: inherit;
`;

const AboutText = styled(LogoText)<{$isRTL: boolean}>`
  position: relative;
  ${({$isRTL}) => ($isRTL ? 'margin-left: 2px;' : 'margin-right: 2px;')}
`;

const YouText = styled(LogoText)<{$isRTL: boolean}>`
  position: absolute;
  top: 0;
  transition: opacity 0.58s ease-out;
  ${({$isRTL}) =>
    $isRTL ? 'right: 0; padding-left: 18px;' : 'left: 0; padding-right: 18px;'}
`;

export const StyledLogoText = styled(LogoText)<{
  $isRTL: boolean;
}>`
  padding-right: 18px;
  max-height: inherit;
  display: flex;
  ${({$isRTL}) =>
    $isRTL
      ? css`
          right: 0;
          margin-left: 0;
          padding: 0px 4px 0px 18px;
        `
      : css`
          left: 0;
          margin-right: 0;
          padding: 0px 18px 0px 4px;
        `}
`;

const UserNameWrapper = styled.span`
  position: relative;
  direction: initial;
  max-height: inherit;
`;

const StyledAboutYouKnob = styled(AboutYouKnob)<{$isRTL: boolean}>`
  position: absolute;
  top: 6px;
  ${({$isRTL}) => ($isRTL ? 'left: 5px;' : 'right: 5px;')}
`;

const StyledAboutYouHeartWhite = styled(AboutYouHeartIcon)<{$isRTL: boolean}>`
  position: absolute;
  top: 3px;
  ${({$isRTL}) => ($isRTL ? 'left: 2px;' : 'right: 2px;')}
`;
